import { Boton } from '@ui/primitivas/Boton'
import { CLASES_DE_NIVEL, describirProcedencia, ETIQUETA_DE_ESTADO, ETIQUETA_DE_PRELACION } from './etiquetas'
import type { Fila, Tarea } from './tipos'

export interface TarjetaDeFilaProps {
  fila: Fila
  tarea: Tarea | undefined
  indice: number
  alVistoBueno: () => void
  alEditar: () => void
  alDescartar: () => void
  alRecuperar: () => void
}

/** Una fila de la MIPER con sus cuatro acciones: visto bueno · editar · descartar (· recuperar). */
export function TarjetaDeFila(props: TarjetaDeFilaProps) {
  const { fila: f, tarea: t, indice } = props
  const e = f.evaluacion
  const pendiente = f.estado === 'propuesta'
  const historica = f.version_motor !== 'evaluacion-isp-v3'
  return (
    <article
      id={`tarjeta-fila-${f.id}`}
      className={`grid gap-3 rounded-lg border p-3 sm:p-4 ${pendiente ? 'border-atencion/60 bg-atencion-fondo/30' : f.estado === 'descartada' ? 'border-borde opacity-80' : 'border-borde bg-superficie'}`}
      aria-labelledby={`fila-${f.id}`}
      data-estado={f.estado}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-1">
          <span className="text-pequeno text-texto-secundario">
            <strong className="text-texto">R{indice + 1}</strong> · {t ? `${t.proceso} · ${t.area} · ${t.puesto}` : 'Tarea'} ·{' '}
            <span className={`cs-state ${pendiente ? 'cs-state--soon' : f.estado === 'validada' ? 'cs-state--live' : 'cs-state--dev'}`}>{ETIQUETA_DE_ESTADO[f.estado]}</span>
          </span>
          <h4 id={`fila-${f.id}`} className="text-cuerpo font-semibold">
            {f.peligro}
          </h4>
          <span className="text-pequeno text-texto-secundario">Tarea: {t?.tarea ?? '—'}</span>
        </div>
        {e.tipo === 'motor' ? (
          <span className={`sst-nivel ${CLASES_DE_NIVEL[e.nivel]}`}>
            {historica ? 'Reevaluación pendiente' : <>VEP {e.vep} · {e.nivel_etiqueta}</>}
          </span>
        ) : (
          <span className={`sst-nivel ${e.consolidada ? 'bg-conforme-fondo text-conforme-texto' : 'bg-informativo-fondo text-informativo-texto'}`}>
            {e.consolidada ? `${e.magnitud_exposicion} · ${e.nivel_riesgo}` : 'Pendiente de protocolo'}
          </span>
        )}
      </div>

      <dl className="grid gap-2 text-pequeno md:grid-cols-2">
        <div>
          <dt className="text-etiqueta text-texto-secundario">RIESGO ESPECÍFICO</dt>
          <dd>
            {f.clasificacion} → {f.familia} → <strong>{f.riesgo}</strong>
          </dd>
        </div>
        <div>
          <dt className="text-etiqueta text-texto-secundario">EVALUACIÓN</dt>
          <dd>
            {historica ? (
              <>Esta fila fue creada con una evaluación anterior. Edítala y selecciona P/C de la guía ISP para usarla en una nueva revisión.</>
            ) : e.tipo === 'motor' ? (
              <>
                P {e.probabilidad} × C {e.consecuencia} = VEP {e.vep}. {e.accion}
                {e.avisos.includes('consecuencia_extrema_con_vep_moderado') ? (
                  <span className="block text-atencion-texto">Aviso: consecuencia extrema con VEP moderado; vigila las condiciones que justificaron P baja.</span>
                ) : null}
              </>
            ) : e.consolidada ? (
              <>
                Consolidada desde {e.fuente ?? 'informe técnico'}.
              </>
            ) : (
              <>Se evalúa con protocolo: {e.protocolo_sugerido ?? 'el aplicable'}. Edita la fila para consolidar el resultado del informe técnico.</>
            )}
          </dd>
        </div>
        {f.justificacion ? (
          <div className="md:col-span-2">
            <dt className="text-etiqueta text-texto-secundario">JUSTIFICACIÓN</dt>
            <dd>{f.justificacion}</dd>
          </div>
        ) : null}
        <div className="md:col-span-2">
          <dt className="text-etiqueta text-texto-secundario">MEDIDAS DE CONTROL</dt>
          <dd>
            {f.medidas.length ? (
              <ul className="grid gap-1">
                {f.medidas.map((m, i) => (
                  <li key={i}>
                    <span className="rounded-pill bg-informativo-fondo px-2 text-etiqueta text-informativo-texto">{ETIQUETA_DE_PRELACION[m.prelacion]}</span> {m.descripcion}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-atencion-texto">Sin medidas: edita la fila para agregarlas.</span>
            )}
          </dd>
        </div>
      </dl>

      <p className="text-pequeno text-texto-secundario">{describirProcedencia(f.origen, f.procedencia)}{f.validada_por ? ` Visto bueno registrado.` : ''}{f.motivo_descarte ? ` Motivo de descarte: ${f.motivo_descarte}.` : ''}</p>

      <div className="flex flex-wrap justify-end gap-2">
        {f.estado === 'descartada' ? (
          <Boton variante="secundario" compacto alPulsar={props.alRecuperar} etiquetaAccesible={`Recuperar la fila ${indice + 1}`}>Recuperar</Boton>
        ) : (
          <>
            {pendiente && !historica ? <Boton variante="afirmativo" compacto alPulsar={props.alVistoBueno} etiquetaAccesible={`Visto bueno a la fila ${indice + 1}`}>✓ Visto bueno</Boton> : null}
            <Boton variante="secundario" compacto alPulsar={props.alEditar} etiquetaAccesible={`Editar la fila ${indice + 1}`}>Editar</Boton>
            <Boton variante="peligro" compacto alPulsar={props.alDescartar} etiquetaAccesible={`Descartar la fila ${indice + 1}`}>Descartar</Boton>
          </>
        )}
      </div>
    </article>
  )
}
